import {
  SigninRedirectArgs,
  SignoutRedirectArgs,
  UserManager,
  UserManagerSettings,
} from 'oidc-client-ts';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import API from '@savgroup-front-common/configuration/api';
import {
  logError,
  logVerbose,
} from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { AuthConfiguration } from '@savgroup-front-common/types';

import { getInitialLocale } from '../helpers';

import {
  REVERS_TRANSLATE_RESOURCE,
  REVERS_TRANSLATE_SCOPE,
} from './dynamicTermSuggestion/dynamicTermSuggestion.configuration';
import { isLogtoEnabled } from './isLogtoEnabled';
import { isUsingB2c } from './isUsingB2c';
import { isUsingGoogleSso } from './isUsingGoogleSso';

export let userManager: UserManager;
export let globalAuthConfiguration: AuthConfiguration;

const CORE_API_SCOPE = 'openid email offline_access profile api';

const buildUserManagerSettings = (
  authConfiguration: AuthConfiguration,
): UserManagerSettings => {
  const url = window.location.origin;

  globalAuthConfiguration = authConfiguration;

  const silent_redirect_uri = `${url}/silent-renew`;
  let post_logout_redirect_uri = authConfiguration.post_logout_redirect_uri
    ? authConfiguration.post_logout_redirect_uri
    : `${url}/index`;

  if (isLogtoEnabled()) {
    if (isUsingB2c()) {
      const finalPostLogoutRedirectUrl = encodeURI(post_logout_redirect_uri);

      post_logout_redirect_uri = encodeURI(
        `${API.b2c}.onmicrosoft.com/b2c_1_susi/oauth2/v2.0/logout?post_logout_redirect_uri=${finalPostLogoutRedirectUrl}`,
      );
    }

    if (isUsingGoogleSso()) {
      post_logout_redirect_uri = encodeURI(
        `https://www.google.com/accounts/Logout`,
      );
    }

    return {
      client_id: authConfiguration.clientId,
      redirect_uri: `${url}/callback`,
      response_type: 'code',
      response_mode: 'query',
      scope: REVERS_TRANSLATE_RESOURCE
        ? `${CORE_API_SCOPE} ${REVERS_TRANSLATE_SCOPE}`
        : CORE_API_SCOPE,
      resource: [
        APIConfiguration.apiBase,
        ...(REVERS_TRANSLATE_RESOURCE ? [REVERS_TRANSLATE_RESOURCE] : []),
      ],
      extraTokenParams: {
        resource: APIConfiguration.apiBase,
      },
      extraQueryParams: authConfiguration.direct_sign_in
        ? {
            direct_sign_in: authConfiguration.direct_sign_in,
          }
        : undefined,
      authority: authConfiguration.authority,
      prompt: 'consent',
      post_logout_redirect_uri,
      silent_redirect_uri,
      loadUserInfo: false,
      filterProtocolClaims: true,
      revokeTokensOnSignout: true,
      automaticSilentRenew: true,
      accessTokenExpiringNotificationTimeInSeconds: 10 * 60,
      staleStateAgeInSeconds: 60,
      ui_locales: getInitialLocale(),
      monitorSession: false,
    };
  }

  return {
    client_id: authConfiguration.clientId,
    redirect_uri: `${url}/callback`,
    response_type: 'code',
    response_mode: 'query',
    scope: authConfiguration.scope,
    authority: authConfiguration.authority,
    post_logout_redirect_uri,
    silent_redirect_uri,
    loadUserInfo: false,
    filterProtocolClaims: true,
    revokeTokensOnSignout: true,
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 10 * 60,
    staleStateAgeInSeconds: 60,
    ui_locales: getInitialLocale(),
    monitorSession: false,
  };
};

export const generateUserManager = (authConfiguration: AuthConfiguration) => {
  userManager = new UserManager(buildUserManagerSettings(authConfiguration));

  if (isLogtoEnabled()) {
    const originalUseRefreshToken =
      userManager['_useRefreshToken'].bind(userManager);

    userManager['_useRefreshToken'] = async (user: any) => {
      const settings = userManager.settings;

      if (!settings.extraQueryParams) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        settings.extraQueryParams = {};
      }

      settings.extraQueryParams.resource ??= APIConfiguration.apiBase;

      if (!user.scope || user.scope.trim() === '') {
        user.scope = CORE_API_SCOPE;
        user.scopes = user.scope.split(' ');
      }

      logVerbose(
        'OIDC PATCH: forcing resource and scope for refresh_token flow',
      );

      return await originalUseRefreshToken(user);
    };
  }

  return userManager;
};

export async function handleSignInRedirectCallback(url?: string) {
  const user = await userManager.signinRedirectCallback(url);

  return user;
}

export const signinSilent = async (args?: SigninRedirectArgs) => {
  const user = await userManager.signinSilent(args);

  return user;
};
export const login = async (args?: SigninRedirectArgs) => {
  return userManager.signinRedirect(args);
};
export const logout = async (args?: SignoutRedirectArgs) => {
  try {
    const user = await userManager.getUser();

    await userManager.removeUser();
    userManager.signoutRedirect({
      id_token_hint: user?.id_token,
      post_logout_redirect_uri: userManager.settings.post_logout_redirect_uri,
      ...args,
    });
  } catch (error) {
    logError(error);
  } finally {
    if (isLogtoEnabled()) {
      sessionStorage.clear();

      const postLogoutRedirectUri = `${window.location.origin}/index`;

      if (isUsingB2c()) {
        const btcPostLogoutRedirectUri = encodeURI(
          `${API.b2c}.onmicrosoft.com/b2c_1_susi/oauth2/v2.0/logout?post_logout_redirect_uri=${postLogoutRedirectUri}`,
        );

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line max-len
        window.location = `${globalAuthConfiguration.authority}/session/end?client_id=${globalAuthConfiguration.clientId}&post_logout_redirect_uri=${btcPostLogoutRedirectUri}`;
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line max-len
        window.location = `${globalAuthConfiguration.authority}/session/end?client_id=${globalAuthConfiguration.clientId}&post_logout_redirect_uri=${postLogoutRedirectUri}`;
      }
    }
  }
};

export const getUser = async () => {
  const user = await userManager.getUser();

  return user;
};

export default (authConfiguration: AuthConfiguration) => {
  return {
    userManagerConfig: () => buildUserManagerSettings(authConfiguration),
    userManager: generateUserManager(authConfiguration),
  };
};
